import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, TextField } from '@mui/material';
import { apiErrorMessage } from '../../../api/http';
import { FormError, ModalActions } from '../modalStyles';

export interface IAnuncioFormData {
	title: string;
	description: string;
}

interface AnadirAnuncioModalProps {
	open: boolean;
	initialData?: IAnuncioFormData;
	onClose: () => void;
	onConfirmar: (data: IAnuncioFormData) => void | Promise<void>;
}

const AnadirAnuncioModal: React.FC<AnadirAnuncioModalProps> = ({
	open,
	initialData,
	onClose,
	onConfirmar,
}) => {
	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const isEditMode = !!initialData;

	useEffect(() => {
		if (!open) {
			setTitle('');
			setDescription('');
			setError(null);
			setSaving(false);
			return;
		}
		setTitle(initialData?.title ?? '');
		setDescription(initialData?.description ?? '');
		setError(null);
		setSaving(false);
		// initialData is read only when the dialog opens, so typing does not reset the fields.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const handleClose = () => {
		if (saving) return;
		onClose();
	};

	const handleConfirmar = async () => {
		if (!title.trim() || !description.trim() || saving) return;
		setSaving(true);
		setError(null);
		try {
			await onConfirmar({ title: title.trim(), description: description.trim() });
		} catch (err) {
			setError(apiErrorMessage(err));
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
			<DialogTitle>{isEditMode ? 'Editar anuncio' : 'Añadir anuncio'}</DialogTitle>
			<DialogContent>
				<TextField
					fullWidth
					label='Título *'
					value={title}
					onChange={(e) => setTitle(e.target.value)}
					margin='normal'
					autoFocus
					slotProps={{ htmlInput: { maxLength: 160 } }}
				/>
				<TextField
					fullWidth
					label='Descripción *'
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					margin='normal'
					multiline
					rows={3}
					slotProps={{ htmlInput: { maxLength: 2000 } }}
				/>
				{error && <FormError severity='error'>{error}</FormError>}
			</DialogContent>
			<ModalActions>
				<Button variant='outlined' onClick={handleClose} disabled={saving}>
					Cancelar
				</Button>
				<Button
					variant='contained'
					onClick={() => void handleConfirmar()}
					disabled={!title.trim() || !description.trim() || saving}
				>
					{saving ? 'Guardando...' : isEditMode ? 'Guardar cambios' : 'Añadir'}
				</Button>
			</ModalActions>
		</Dialog>
	);
};

export default AnadirAnuncioModal;
